import * as React from 'react';
import type { SmartSwapProduct, Product } from '@/types';
import { Link } from '@tanstack/react-router';
import {StoreLogo} from "@/web/components/molecules/StoreLogo.tsx";

export interface SmartSwapProductCardProps {
  smartSwap: SmartSwapProduct;
}

const getProductImage = (product: Product): React.ReactElement | null => {
  if (product.imageUrls && product.imageUrls.length) {
    return (<img src={product.imageUrls[0]} alt={product.name} />);
  }
  return null;
}

const convertCentsToDollars = (cents: number): string => {
  return (cents / 100).toFixed(2);
}

const formatScore = (score: number | undefined | null): string | null => {
  if (score === undefined || score === null) return null;
  return score.toFixed(3);
}

const showComparativePricing = (product: Product): React.ReactElement | null => {
  if (product.price && product.price.comparativePricing) {
    return (
      <h3 className="mt-2 text-sm text-muted-foreground">
        ${convertCentsToDollars(product.price.comparativePricing.priceAsCents)} per {product.price.comparativePricing.quantity}{product.price.comparativePricing.unitOfMeasure}
      </h3>
    )
  }
  return null;
}

export function SmartSwapProductCard({ smartSwap }: SmartSwapProductCardProps) {
  const { product } = smartSwap;
  
  const scores = [
    { label: 'Product', value: smartSwap.productSimilarityScore },
    { label: 'Ingredients', value: smartSwap.ingredientsSimilarityScore },
    { label: 'Allergens', value: smartSwap.allergensSimilarityScore },
    { label: 'Overall', value: smartSwap.overallSimilarityScore },
  ];

  return (
    <div className="product-card border rounded-lg p-4 flex flex-col gap-4 relative">
        <div className="flex gap-4">
            <div className="product-image w-24 h-24 flex-shrink-0 bg-gray-50 rounded overflow-hidden flex items-center justify-center">
                {getProductImage(product)}
            </div>
            <div className="product-details flex-1">
                <div className="product-brand text-xs text-muted-foreground uppercase font-semibold">
                    {product.brand}
                </div>
                <Link
                    to="/products/$categoryId/details/$productId"
                    params={{
                        categoryId: product.categories.level0,
                        productId: product.productId,
                    }}
                    className="text-lg font-semibold hover:underline cursor-pointer text-blue-600 block"
                >
                    {product.name}
                </Link>
                <h1 className="text-xl font-bold mt-1">
                    ${convertCentsToDollars(product.price.amountAsCents)}
                </h1>
                {showComparativePricing(product)}
                <div className="product-id text-xs text-muted-foreground mt-1">
                    {product.productId}
                </div>
            </div>
        </div>
        <div className="flex gap-4">
            <div className="w-24 flex-shrink-0">
                <StoreLogo product={product} />
            </div>
            <div className="flex-1">
                {scores.map((score) => {
                    const formatted = formatScore(score.value);
                    if (formatted === null) return null;
                    return (
                        <span
                            key={score.label}
                            className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium"
                            title={`${score.label} Similarity Score`}
                        >
              {score.label}: {formatted}
            </span>
                    );
                })}
            </div>
        </div>
    </div>
  );
}
